// Die Geometrie des Technologiebaums — ohne Phaser, ohne DOM.
//
// `TechTreeScene` importiert Phaser und ist damit ohne Browser nicht testbar.
// Alles, was sich **rechnen** laesst — Rastermass, Ausdehnung, Einpassung,
// Trefferabfrage und der **Zustand eines Knotens** — liegt deshalb hier, in
// Schicht 3. Die Szene bleibt der Zeichen- und Eingabepfad; die Zahlen
// dahinter sind geprueft.
//
// Der Anlass war handfest: `centerOn(0, 0)` in `create()` zentrierte auf den
// ersten Knoten, waehrend die Kamera noch den ganzen Canvas mass. Nach
// `setViewport` stimmte der Ausschnitt nicht mehr, und der Spieler sah eine
// leere Flaeche **neben** dem Baum. Wer den Ausschnitt einpasst, muss die
// Ausdehnung kennen — und die kommt aus den Daten, nicht aus einer Vermutung.

import { clamp } from '../core/math';
import type { NodeState, TreeNode } from '../data/types';
import { builtIds, nodeCost, nodeUnlocked } from './stats';

/** Rastermass einer Baumzelle in Weltpixeln. */
export const TREE_CELL = 84;

/** Kantenlaenge eines Knotenfeldes in Weltpixeln. */
export const TREE_NODE_SIZE = 46;

/** Grenzen des Kamerazooms. Unten weit genug fuer ein Handy im Hochformat. */
export const TREE_ZOOM_MIN = 0.35;
export const TREE_ZOOM_MAX = 1.8;

/** Luft zwischen Baum und Ausschnittrand, in Weltpixeln. */
const PADDING = 28;

export interface TreeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

/**
 * Die Ausdehnung des Baums in Weltkoordinaten, Knotenkanten eingerechnet.
 *
 * Ohne Knoten gibt es einen leeren Bereich um den Ursprung zurueck — ein
 * Rueckfallwert ist hier billiger als ein Sonderfall in der Szene.
 */
export function treeBounds(entries: readonly TreeNode[]): TreeBounds {
  const half = TREE_NODE_SIZE / 2;
  if (entries.length === 0) return boundsFrom(-half, -half, half, half);

  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const entry of entries) {
    const x = entry.x * TREE_CELL;
    const y = entry.y * TREE_CELL;
    if (x - half < left) left = x - half;
    if (y - half < top) top = y - half;
    if (x + half > right) right = x + half;
    if (y + half > bottom) bottom = y + half;
  }
  return boundsFrom(left, top, right, bottom);
}

function boundsFrom(left: number, top: number, right: number, bottom: number): TreeBounds {
  return {
    left, top, right, bottom,
    width: right - left,
    height: bottom - top,
    centerX: (left + right) / 2,
    centerY: (top + bottom) / 2,
  };
}

/**
 * Der Zoom, bei dem der ganze Baum in einen Ausschnitt passt.
 *
 * Bewusst **kein** ganzzahliger Zoom: der Pixel-Look-Vertrag gilt der Welt
 * (`VIEW-001`), nicht dem Baum — dort waeren die drei erlaubten Stufen 1, 2, 3
 * und die kleinste zeigte auf einem Handy ein Viertel des Baums.
 */
export function fitZoom(bounds: TreeBounds, width: number, height: number): number {
  if (width <= 0 || height <= 0) return 1;
  const zoom = Math.min(
    width / (bounds.width + PADDING * 2),
    height / (bounds.height + PADDING * 2),
  );
  return clamp(zoom, TREE_ZOOM_MIN, TREE_ZOOM_MAX);
}

/**
 * Der Knoten unter einem Weltpunkt, oder `null`.
 *
 * Quadratische Trefferflaeche, weil die Knoten Quadrate sind — und die erste
 * Uebereinstimmung gewinnt, weil sich zwei Knoten nach Gate FG-22 nie
 * dieselbe Rasterposition teilen.
 */
export function nodeAt(
  entries: readonly TreeNode[],
  worldX: number,
  worldY: number,
): TreeNode | null {
  const half = TREE_NODE_SIZE / 2;
  for (const entry of entries) {
    if (Math.abs(worldX - entry.x * TREE_CELL) > half) continue;
    if (Math.abs(worldY - entry.y * TREE_CELL) > half) continue;
    return entry;
  }
  return null;
}

/**
 * Die Ausgangslage fuer die Zustandsfrage — **einmal** gebildet, nicht je Knoten.
 *
 * `builtIds` baut eine Menge; sie fuer jeden der 47 Knoten neu zu bauen waere
 * 47-mal dieselbe Arbeit in einem Pfad, der bei offenem Panel jeden Frame
 * laeuft.
 */
export interface TreeStatus {
  levels: Record<string, number>;
  built: ReadonlySet<string>;
  shipLevel: number;
  res: Record<string, number>;
  /** Erledigte Gegner je Art — der dritte Riegel (`needKill`). */
  kills: Readonly<Record<string, number>>;
}

/** Nur die Felder, die der Baum liest — deshalb kennt `sim` den Spielstand nicht. */
export interface TreeSave {
  levels: Record<string, number>;
  built: number;
  ship: { level: number };
  res: Record<string, number>;
}

/**
 * Die Felder, die der Baum liest — plus die erlegten Gegner.
 *
 * `kills` steht als **eigener Parameter** und nicht in `TreeSave`: die
 * Statistik ist im Spielstand eine Karte gemischter Werte (Zahlen **und**
 * Aufschlüsselungen), und `sim` soll sie nicht auseinandersortieren müssen. Der
 * Aufrufer reicht die eine Karte herein, die hier zählt.
 */
export function treeStatus(
  save: TreeSave, kills: Readonly<Record<string, number>> = {},
): TreeStatus {
  return {
    levels: save.levels,
    built: builtIds(save.built),
    shipLevel: save.ship.level,
    res: save.res,
    kills,
  };
}

/**
 * Der Zustand eines Knotens — die **eine** Auswertung hinter Farbe, Legende
 * und Sperrgrund.
 *
 * Sie liegt hier und nicht in der Szene, weil sie sich rechnen laesst: die
 * Szene braucht einen Browser, diese Funktion einen Spielstand. Und weil die
 * Legende im DOM dieselbe Antwort braucht — zwei Auswertungen waeren zwei
 * Wahrheiten, sichtbar als „die Legende zaehlt drei kaufbare, ich sehe zwei".
 */
export function nodeState(entry: TreeNode, status: TreeStatus): NodeState {
  const level = status.levels[entry.id] ?? 0;
  if (!nodeUnlocked(entry, status.levels, status.built, status.shipLevel, status.kills)) {
    return 'locked';
  }
  if (level >= entry.max) return 'max';
  return (status.res[entry.cost.res] ?? 0) >= nodeCost(entry, level) ? 'buyable' : 'available';
}

/** Wie viele Knoten in welchem Zustand stehen — die Zahlen der Legende. */
export function countStates(
  entries: readonly TreeNode[],
  status: TreeStatus,
): Record<NodeState, number> {
  const out: Record<NodeState, number> = { locked: 0, available: 0, buyable: 0, max: 0 };
  for (const entry of entries) out[nodeState(entry, status)] += 1;
  return out;
}

// ── Die Ausbaukette ──────────────────────────────────────────────────────────
//
// Sie steht hier und nicht in `ui/` oder `net/`, weil beide sie brauchen: der
// Zug-Dispatcher, um den Kauf abzulehnen, und das Panel, um zu sagen **warum**.
// Zwei Fassungen davon wären die zweite Regelquelle, gegen die `oberflaeche.md`
// geschrieben ist — die Anzeige sagte dann „gesperrt", und die Wirkung meinte
// etwas anderes.

/** Was eine Ausbaustufe für einen Spielstand gerade ist. */
export type BuildState = 'done' | 'open' | 'era' | 'locked';

export interface BuildSave {
  built: number;
  era: number;
}

/**
 * Zustand einer Stufe an ihrer Position in der Kette.
 *
 * `'era'` ist der Riegel an der Naht: die Stufe ist an der Reihe, aber sie
 * gehört zum Wiederaufbau. Sie **als gesperrt** zu zeigen wäre nicht falsch,
 * nur nutzlos — der Spieler sähe dieselbe Anzeige wie bei einer Stufe, die
 * einfach noch nicht dran ist, und hätte keinen Hinweis auf den Sprung.
 */
export function buildState(save: BuildSave, index: number, minEra: number): BuildState {
  if (index < save.built) return 'done';
  if (index > save.built) return 'locked';
  return minEra > save.era ? 'era' : 'open';
}

/** Warum diese Stufe nicht gebaut werden kann — leer heißt: sie kann. */
export function buildLocked(state: BuildState): string {
  if (state === 'era') return 'Diese Stufe gehört zum Wiederaufbau — sie wartet auf der anderen Seite.';
  if (state === 'open') return '';
  return 'Das ist nicht die nächste Stufe.';
}
